import { resolveTheme } from '@matter_extractor/shared';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { type PageReaderProps, READER_TEST_IDS } from './contracts';
import { FeedbackThanks } from './feedback-thanks';
import { loadExperienceFonts } from './fonts';
import { ReaderBody } from './reader-body';
import { asStyle, readerMotion, readerThemeStyle } from './reader-theme';
import { usePrefersReducedMotion } from './use-reduced-motion';
import './reader.css';

/**
 * THE reader: one page of an experience, played (§9.2, §9.3, §9.4, §9.5).
 *
 * Three surfaces mount it and it is the same component in all three - the
 * preview of the page editor, the scene of the Timeline, the session - because
 * « le rendu doit être le même dans l'éditeur et dans la session », and that is
 * what makes it a component mounted out of its page rather than a screen. The
 * contract, mode by mode, is in `contracts.ts`.
 *
 * ## What this file owns, and what it hands down
 *
 * The THEME (§9.4): it merges the experience's tokens with the page's, turns
 * the answer into `--lecteur-*` properties on the root, asks for the two faces
 * the theme names, and paints the whole frame - « le thème peint toute la
 * fenêtre en format téléphone ». The body owns the temps, the blocks and the
 * presses.
 *
 * ## No request leaves this component
 *
 * A file of the media library is reached by `ambianceFileUrl`, which builds an
 * address; the elements fetch it. The reader has nothing to load, so it has
 * nothing to be loading, and a preview never spends a first frame on a spinner.
 *
 * ## When the appearance plays
 *
 * `interactive` plays from the mount. `edit` shows the page whole - one does
 * not edit through an animation - and plays only when `replayKey` CHANGES,
 * which is « Rejouer l'apparition ». `static` never plays. The ref below is
 * read and never written: `useRef` seeds it at the first render, so it holds
 * the mount's key for the life of the mount, and comparing against it is how a
 * replay is told apart from a first paint without an effect and without state.
 *
 * ## The phrase of thanks takes the page's place (issue #176)
 *
 * `thanks` given, the blocks are not drawn at all and the frame - the theme's
 * background, veil and face - holds the phrase instead
 * (`feedback-thanks.tsx`). It is the same root, so the window does not repaint
 * between the page and its thanks; only the column inside changes.
 *
 * ## HOW MANY temps are shown may be taken over from outside
 *
 * `revealed` / `onRevealed` is a controlled / uncontrolled pair handed straight
 * down to the body (issue #141). This file decides nothing about it: it is the
 * body that holds the count when nobody claims it, and the body that asks for
 * one more when it is claimed. Passing it through rather than intercepting it
 * keeps ONE place in the folder that knows what « un temps de plus » means.
 */
export function PageReader({
  page,
  theme,
  format,
  mode,
  values,
  selectedBlockId = null,
  onSelectBlock,
  onValueChange,
  onAdvance,
  replayKey,
  resolveVariable,
  variableNames,
  revealed,
  onRevealed,
  conditions,
  conditionMark,
  noyau,
  waitingBlockId = null,
  thanks = null,
}: PageReaderProps): ReactNode {
  const pageVisual = useMemo(() => resolveTheme(theme, page.visual), [theme, page.visual]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const motion = readerMotion(pageVisual, prefersReducedMotion);

  const mountReplayKey = useRef(replayKey);
  const play = mode === 'interactive' || (mode === 'edit' && replayKey !== mountReplayKey.current);

  /* « N'est chargée que si un thème la nomme » (§9.4). A failure is handled
   * inside - the page stays readable in its fallback stack - so nothing here
   * awaits it or shows anything while it is in flight. */
  useEffect(() => {
    void loadExperienceFonts(pageVisual);
  }, [pageVisual]);

  /*
   * The first gesture, for the Son blocks (§9.2): every browser refuses a sound
   * on a page nobody has touched. Captured on the root so ANY gesture counts,
   * including the click that answers a question, and `capture` so a press that
   * stops propagation still arms it.
   */
  const [gestured, setGestured] = useState(false);
  const arm = (): void => {
    if (mode === 'interactive') setGestured(true);
  };

  return (
    <div
      className="lecteur"
      data-testid={READER_TEST_IDS.root}
      data-format={format}
      data-mode={mode}
      data-motion={motion}
      data-play={play ? 'on' : 'off'}
      style={asStyle(readerThemeStyle(pageVisual, format))}
      inert={mode === 'static'}
      onPointerDownCapture={arm}
      onKeyDownCapture={arm}
    >
      {thanks === null ? (
        <ReaderBody
          key={`${page.id}:${String(replayKey ?? '')}`}
          page={page}
          theme={theme}
          mode={mode}
          motion={motion}
          play={play}
          gestured={gestured}
          values={values}
          selectedBlockId={selectedBlockId}
          onSelectBlock={onSelectBlock}
          onValueChange={onValueChange}
          onAdvance={onAdvance}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
          revealed={revealed}
          onRevealed={onRevealed}
          conditions={conditions}
          conditionMark={conditionMark}
          noyau={noyau}
          waitingBlockId={waitingBlockId}
        />
      ) : (
        <FeedbackThanks text={thanks} />
      )}
    </div>
  );
}
